import React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const Sidebar = ({
  papers,
  selectedPaper,
  onPaperSelect,
  allResponses, // Still needed for the 'submitted' status
  papersProgress, // The new live progress map
  collapsed,
  onToggleCollapse,
}) => {
  const getPaperStatus = (paperId) => {
    const paperData = allResponses[paperId];
    if (paperData?.submitted) {
      return { icon: '🔒', percentage: 100 };
    }

    const progress = papersProgress[paperId] || { percentage: 0 };
    const percentage = Math.round(progress.percentage);

    let icon = '⚪'; // Not started
    if (percentage > 0 && percentage < 100) {
      icon = '📝'; // In progress
    } else if (percentage === 100) {
      icon = '✅'; // Complete (but not submitted)
    }

    return { icon, percentage };
  };

  return (
    <div
      className={`bg-slate-100 border-r border-slate-200 transition-all duration-300 ease-in-out ${
        collapsed ? 'w-16' : 'w-80'
      } flex flex-col shadow-sm flex-shrink-0`}
    >
      <div className="p-3 border-b border-slate-200 bg-white flex-shrink-0">
        <button
          onClick={onToggleCollapse}
          className="w-full flex items-center justify-center p-2 rounded-lg hover:bg-slate-100 transition-colors duration-200"
          title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {collapsed ? (
            <ChevronRight size={20} className="text-slate-600" />
          ) : (
            <div className="flex items-center justify-between w-full">
              <span className="text-slate-900 font-semibold">📝 Papers</span>
              <ChevronLeft size={20} className="text-slate-600" />
            </div>
          )}
        </button>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto custom-scrollbar p-3">
        {collapsed ? (
          <div className="space-y-2">
            {papers.map((paper, index) => {
              const { icon } = getPaperStatus(paper.id);
              return (
                <button
                  key={paper.id}
                  className={`w-full p-2 rounded-lg transition-all duration-200 ${
                    selectedPaper?.id === paper.id
                      ? 'bg-blue-600 text-white shadow-md'
                      : 'bg-white hover:bg-blue-50 text-slate-700 border border-slate-200'
                  }`}
                  onClick={() => onPaperSelect(paper)}
                  title={`${index + 1}. ${paper.title}`}
                >
                  <div className="flex flex-col items-center">
                    <span
                      className={`text-lg ${
                        icon === '🔒' ? 'text-yellow-600' : ''
                      }`}
                    >
                      {icon}
                    </span>
                    <span className="text-xs mt-1">{index + 1}</span>
                  </div>
                </button>
              );
            })}
          </div>
        ) : (
          <div className="space-y-3">
            <h3 className="text-slate-700 font-semibold text-sm uppercase tracking-wider px-1">
              {papers.length > 0
                ? `Your Papers (${papers.length})`
                : 'Your Papers'}
            </h3>
            {papers.length === 0 && (
              <div className="text-center py-6 px-2">
                <p className="text-sm text-slate-500">
                  No papers assigned to you yet.
                </p>
              </div>
            )}
            <div className="space-y-2">
              {papers.map((paper, index) => {
                const { icon, percentage } = getPaperStatus(paper.id);
                return (
                  <button
                    key={paper.id}
                    className={`w-full p-4 rounded-lg text-left transition-all duration-200 group ${
                      selectedPaper?.id === paper.id
                        ? 'bg-blue-600 text-white shadow-lg'
                        : 'bg-white hover:bg-blue-50 text-slate-700 border border-slate-200 hover:border-blue-200'
                    }`}
                    onClick={() => onPaperSelect(paper)}
                  >
                    <div className="flex items-start gap-3">
                      <span
                        className={`text-lg flex-shrink-0 mt-1 ${
                          icon === '🔒' ? 'text-yellow-500' : ''
                        }`}
                      >
                        {icon}
                      </span>
                      <div className="flex-1 min-w-0">
                        <p
                          className={`text-sm font-medium leading-tight mb-2 ${
                            selectedPaper?.id === paper.id
                              ? 'text-white'
                              : 'text-slate-900'
                          }`}
                        >
                          {index + 1}. {paper.title}
                        </p>

                        <div
                          className={`w-full h-1.5 rounded-full ${
                            selectedPaper?.id === paper.id
                              ? 'bg-blue-500'
                              : 'bg-slate-200'
                          }`}
                        >
                          <div
                            className={`h-full rounded-full transition-all duration-300 ${
                              selectedPaper?.id === paper.id
                                ? 'bg-blue-200'
                                : 'bg-blue-500'
                            }`}
                            style={{ width: `${percentage}%` }}
                          ></div>
                        </div>
                        <span
                          className={`text-xs font-medium mt-1 ${
                            selectedPaper?.id === paper.id
                              ? 'text-blue-100'
                              : 'text-slate-500'
                          }`}
                        >
                          {percentage}% complete
                        </span>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {!collapsed && (
        <div className="p-3 border-t border-slate-200 bg-slate-50 flex-shrink-0">
          <div className="text-xs text-slate-600 text-center">
            <div className="mb-1 font-semibold">Status Legend:</div>
            <div className="flex justify-center gap-3">
              <span>⚪ Not started</span>
              <span>📝 In progress</span>
              <span>✅ Complete</span>
              <span>🔒 Submitted</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Sidebar;
